import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { ROUTES } from '@/routes/paths';
import {
  useDocumentChain,
  asAlreadyInvoiced,
} from '@/shared/hooks/useDocumentChain';

/**
 * "Create Tax Invoice", for a Quotation, a Proforma, or a walk-in direct sale.
 *
 * This raises a DRAFT invoice and sends the user to the Invoices list, where it
 * shows as "Draft — not posted" with a Finalize & Post action. The GST number,
 * the ledger postings and the advance-payment gate all happen at Finalize
 * (InvoicesPage), not here — the client asked for the invoice to be editable
 * after creation, and a draft is the only version that can be. (There is no
 * standalone detail route for an AccountsSalesInvoice yet — /invoices/:id is
 * the direct-sale SalesOrder detail page — so the list is the landing point.)
 */

interface Props {
  documentId: string;
  documentType: 'quotation' | 'proforma' | 'direct_sale';
  documentNumber?: string;
  /** Hide the actions on a document nothing can descend from. */
  disabled?: boolean;
  /**
   * Render as one plain button, for sitting inside a page's own action bar
   * rather than as a standalone block. The bar is where every other action on
   * the document already lives, so this one belongs beside them.
   */
  inline?: boolean;
  size?: 'sm' | 'md';
}

export function DocumentChainActions({ documentId, disabled, inline = false, size }: Props) {
  const navigate = useNavigate();
  const { createTaxInvoice } = useDocumentChain();

  const onInvoice = () => {
    createTaxInvoice.mutate({ sourceId: documentId }, {
      onSuccess: (data) => {
        if (data?.needsApproval) {
          toast.success(data.message || 'Invoice request submitted for approval.');
          navigate(ROUTES.invoices);
          return;
        }
        toast.success('Draft invoice created — review it in Invoices, then Finalize & Post.');
        navigate(ROUTES.invoices);
      },
      onError: (err) => {
        const dup = asAlreadyInvoiced(err);
        if (dup) {
          toast.error(dup.error);
          navigate(ROUTES.invoices);
          return;
        }
        toast.error(errText(err, 'Could not create the tax invoice.'));
      },
    });
  };

  const busy = createTaxInvoice.isPending;

  const button = (
    <Button
      onClick={onInvoice}
      disabled={disabled || busy}
      // Outline in the bar: this is one action among several, not the page's
      // primary call to action, and a filled button beside plain ones reads as
      // "press me first" when Send usually is.
      variant={inline ? 'outline' : undefined}
      size={size}
      className={inline ? 'rounded-xl text-xs font-bold' : undefined}
    >
      {busy ? 'Creating…' : 'Create Tax Invoice'}
    </Button>
  );

  if (inline) return button;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">{button}</div>
    </div>
  );
}

function errText(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return msg || fallback;
}
